import { isAuthRetryableFetchError, type SupabaseClient, type Session, type AuthError } from '@supabase/supabase-js';
import type { AuthPort, AuthSession } from '@estudio/desktop-contract';

function toSession(s: Session | null): AuthSession | null {
  if (!s) return null;
  return { userId: s.user.id, email: s.user.email ?? null, accessToken: s.access_token, expiresAt: s.expires_at ?? null };
}

export class SessionCheckError extends Error {
  constructor(readonly offline: boolean) {
    super(offline ? 'No se pudo comprobar tu sesión sin conexión. Tus borradores se conservan; vuelve a intentarlo cuando tengas conexión.' : 'No se pudo comprobar tu sesión. Vuelve a intentarlo; tus borradores se conservan.');
    this.name = 'SessionCheckError';
  }
}

function checkedSession(result: { data: { session: Session | null }; error: AuthError | null }) {
  if (result.error) {
    if (isAuthRetryableFetchError(result.error)) throw new SessionCheckError(true);
    // Only definitive refresh failures establish that reauthentication is needed.
    if (!['refresh_token_not_found', 'refresh_token_already_used', 'invalid_refresh_token', 'session_not_found', 'session_expired'].includes(result.error.code ?? '')) throw new SessionCheckError(false);
  }
  return toSession(result.data.session);
}

export interface RecoverableAuthPort extends AuthPort {
  recoverSession(expected: AuthSession): Promise<AuthSession | null>;
}

/**
 * Acceso y registro por correo verificado. El servidor conserva confirmación
 * de correo y límites; cada nueva identidad recibe su propio espacio al iniciar.
 * En escritorio se usa el retorno PKCE y el almacén seguro del host.
 */
export function createSupabaseAuthPort(supabase: SupabaseClient, emailLink?: import('@estudio/desktop-contract').DesktopInjection['emailLink']): RecoverableAuthPort {
  let generation = 0;
  return {
    async getSession() {
      return checkedSession(await supabase.auth.getSession());
    },
    async recoverSession(expected) {
      const before = generation;
      const current = checkedSession(await supabase.auth.getSession());
      if (!current || current.userId !== expected.userId) return null;
      if (current.accessToken !== expected.accessToken) return current;
      if (generation !== before) return null;
      const refreshed = await supabase.auth.refreshSession();
      const session = checkedSession(refreshed);
      // A concurrent sign-out/account switch must never replay the old operation.
      const latest = checkedSession(await supabase.auth.getSession());
      if (!session || !latest || latest.userId !== expected.userId || latest.accessToken !== session.accessToken) return null;
      return latest;
    },
    async requestEmailCode(email) {
      try {
      const emailRedirectTo = emailLink ? await emailLink.prepare() : typeof window !== 'undefined' ? window.location.origin : undefined;
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true, ...(emailRedirectTo ? { emailRedirectTo } : {}) } });
      if (error) {
        const msg = /signups? not allowed|not found|user not found/i.test(error.message)
          ? 'El servidor no permite crear cuentas en este momento. Inténtalo más tarde.'
          : /email address not authorized|email_address_not_authorized|only.*authorized.*email/i.test(error.message)
            ? 'El servicio de correo todavía no admite este destinatario. Contacta al soporte de PromptCut.'
          : /rate limit/i.test(error.message)
            ? 'Demasiados intentos; espera unos minutos.'
            : 'No se pudo enviar el enlace. Comprueba el correo y tu conexión e inténtalo de nuevo.';
        throw new Error(msg);
      }
      } catch (error) {
        if (error instanceof Error && /^(El servidor|El servicio|Demasiados|No se pudo enviar)/.test(error.message)) throw error;
        throw new Error('No se pudo preparar el acceso. Reinicia PromptCut y solicita un enlace nuevo.', { cause: error });
      }
    },
    async verifyEmailCode(email, code) {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
      if (error || !data.session) throw new Error(error?.message ?? 'Código inválido o vencido.');
      return toSession(data.session)!;
    },
    async signOut() {
      await supabase.auth.signOut();
    },
    onChange(listener) {
      const { data } = supabase.auth.onAuthStateChange((event, s) => {
        // INITIAL_SESSION:null can also mean an offline refresh. The explicit
        // getSession result distinguishes that error from an absent session.
        if (event === 'INITIAL_SESSION' && !s) return;
        generation++;
        listener(toSession(s));
      });
      const off = emailLink?.subscribe((code) => {
        const failed = () => { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('promptcut:auth-error', { detail: 'El enlace venció o pertenece a otro intento. Solicita uno nuevo desde esta aplicación.' })); };
        void supabase.auth.exchangeCodeForSession(code).then(({ error }) => { if (error) failed(); }).catch(failed);
      });
      return () => { data.subscription.unsubscribe(); off?.(); };
    },
  };
}
